import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PricingService } from "@/gen/omnios/v1/pricing_connect";
import { transport } from "@/lib/grpc";
import { useEscapeBack } from "@/hooks/use-escape-back";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Timestamp } from "@bufbuild/protobuf";

export const Route = createFileRoute("/commerce/pricelists/$id")({
	component: PriceListDetailPage,
});

const pricingClient = createClient(PricingService, transport);

function fmtTs(ts: Timestamp | undefined): string {
	if (!ts) return "—";
	return new Date(Number(ts.seconds) * 1000).toLocaleDateString("ru-RU");
}

function PriceListDetailPage() {
	const { t } = useTranslation("commerce");
	const { id } = Route.useParams();
	const qc = useQueryClient();
	useEscapeBack("/commerce/pricing");

	const [removeTarget, setRemoveTarget] = useState<string | null>(null);
	const [showAddRule, setShowAddRule] = useState(false);
	const [ruleForm, setRuleForm] = useState({
		productId: "",
		categoryId: "",
		ruleType: "fixed",
		priceCents: 0,
		discountPct: 0,
		minQty: 0,
	});

	const { data: pl, isLoading } = useQuery({
		queryKey: ["price-list", id],
		queryFn: () => pricingClient.getPriceList({ id }),
	});

	useBreadcrumbs([
		{ label: t("pricelists"), href: "/commerce/pricelists" },
		{ label: pl?.name ?? id },
	]);

	const updateMut = useMutation({
		mutationFn: (params: {
			name?: string;
			isActive?: boolean;
			priority?: number;
			effectiveFrom?: { seconds: bigint; nanos: number };
			effectiveUntil?: { seconds: bigint; nanos: number };
		}) => pricingClient.updatePriceList({ id, ...params }),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["price-list", id] }),
	});

	const addRuleMut = useMutation({
		mutationFn: () =>
			pricingClient.addPriceListRule({
				priceListId: id,
				productId: ruleForm.productId,
				categoryId: ruleForm.categoryId,
				ruleType: ruleForm.ruleType,
				priceCents: BigInt(ruleForm.priceCents),
				discountPct: ruleForm.discountPct,
				minQty: ruleForm.minQty,
			}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["price-list", id] });
			setShowAddRule(false);
			setRuleForm({ productId: "", categoryId: "", ruleType: "fixed", priceCents: 0, discountPct: 0, minQty: 0 });
		},
	});

	const removeRuleMut = useMutation({
		mutationFn: (ruleId: string) =>
			pricingClient.removePriceListRule({ priceListId: id, ruleId }),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["price-list", id] });
			setRemoveTarget(null);
		},
	});

	if (isLoading || !pl) {
		return (
			<div className="flex items-center justify-center py-16">
				<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
			</div>
		);
	}

	const dateToTs = (s: string) =>
		s ? { seconds: BigInt(Math.floor(new Date(s).getTime() / 1000)), nanos: 0 } : undefined;

	return (
		<div className="space-y-6 p-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div className="space-y-1">
					<h2 className="text-lg font-semibold">{pl.name}</h2>
					<div className="flex items-center gap-2 text-sm text-muted-foreground">
						<span>{pl.currency}</span>
						<Badge variant={pl.isActive ? "default" : "outline"} className="text-xs">
							{pl.isActive ? t("pl_active") : t("pl_inactive")}
						</Badge>
						<span>
							{fmtTs(pl.effectiveFrom)} — {fmtTs(pl.effectiveUntil)}
						</span>
					</div>
				</div>
				<div className="flex items-center gap-2">
					<Label className="text-sm text-muted-foreground">{t("pl_col_active")}</Label>
					<Switch
						checked={pl.isActive}
						onCheckedChange={(v) => updateMut.mutate({ isActive: v })}
					/>
				</div>
			</div>

			{/* Edit section */}
			<div className="rounded-lg border p-4">
				<h3 className="mb-3 text-sm font-medium">{t("pl_edit_settings")}</h3>
				<div className="grid grid-cols-2 gap-4 md:grid-cols-4">
					<div className="space-y-1">
						<Label className="text-xs">{t("pricelist_name")}</Label>
						<Input
							defaultValue={pl.name}
							onBlur={(e) => {
								if (e.target.value !== pl.name) {
									updateMut.mutate({ name: e.target.value });
								}
							}}
						/>
					</div>
					<div className="space-y-1">
						<Label className="text-xs">{t("pricelist_priority")}</Label>
						<Input
							type="number"
							defaultValue={pl.priority}
							onBlur={(e) => {
								const v = Number(e.target.value);
								if (v !== pl.priority) {
									updateMut.mutate({ priority: v });
								}
							}}
						/>
					</div>
					<div className="space-y-1">
						<Label className="text-xs">{t("pl_effective_from")}</Label>
						<Input
							type="date"
							defaultValue={
								pl.effectiveFrom
									? new Date(Number(pl.effectiveFrom.seconds) * 1000).toISOString().split("T")[0]
									: ""
							}
							onBlur={(e) => {
								const ts = dateToTs(e.target.value);
								if (ts) updateMut.mutate({ effectiveFrom: ts });
							}}
						/>
					</div>
					<div className="space-y-1">
						<Label className="text-xs">{t("pl_effective_until")}</Label>
						<Input
							type="date"
							defaultValue={
								pl.effectiveUntil
									? new Date(Number(pl.effectiveUntil.seconds) * 1000).toISOString().split("T")[0]
									: ""
							}
							onBlur={(e) => {
								const ts = dateToTs(e.target.value);
								if (ts) updateMut.mutate({ effectiveUntil: ts });
							}}
						/>
					</div>
				</div>
			</div>

			{/* Rules table */}
			<div className="space-y-3">
				<div className="flex items-center justify-between">
					<h3 className="text-sm font-medium">
						{t("pricelist_rules")} ({pl.rules.length})
					</h3>
					<Button size="sm" onClick={() => setShowAddRule(true)}>
						<Plus className="mr-1.5 h-3.5 w-3.5" />
						{t("pl_add_rule")}
					</Button>
				</div>

				{pl.rules.length === 0 ? (
					<p className="py-4 text-center text-sm text-muted-foreground">
						{t("pl_no_rules")}
					</p>
				) : (
					<div className="overflow-hidden rounded-lg border">
						<Table>
							<TableHeader>
								<TableRow className="border-b bg-muted/30">
									<TableHead className="px-3 py-2 text-left font-medium text-muted-foreground">
										{t("pl_rule_product")}
									</TableHead>
									<TableHead className="px-3 py-2 text-left font-medium text-muted-foreground">
										{t("pl_rule_category")}
									</TableHead>
									<TableHead className="w-28 px-3 py-2 text-left font-medium text-muted-foreground">
										{t("rule_type")}
									</TableHead>
									<TableHead className="w-28 px-3 py-2 text-left font-medium text-muted-foreground">
										{t("pl_rule_value")}
									</TableHead>
									<TableHead className="w-20 px-3 py-2 text-left font-medium text-muted-foreground">
										{t("min_qty")}
									</TableHead>
									<TableHead className="w-12 px-3 py-2" />
								</TableRow>
							</TableHeader>
							<TableBody className="divide-y">
								{pl.rules.map((r) => (
									<TableRow key={r.id} className="hover:bg-muted/50">
										<TableCell className="px-3 py-2">
											{r.productId ? (
												<code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
													{r.productId.substring(0, 8)}...
												</code>
											) : (
												"—"
											)}
										</TableCell>
										<TableCell className="px-3 py-2">
											{r.categoryId ? (
												<code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
													{r.categoryId.substring(0, 8)}...
												</code>
											) : (
												"—"
											)}
										</TableCell>
										<TableCell className="px-3 py-2">
											<Badge variant="outline" className="text-xs">
												{r.ruleType === "fixed" && t("rule_fixed")}
												{r.ruleType === "percentage" && t("rule_percentage")}
												{r.ruleType === "tiered" && t("rule_tiered")}
											</Badge>
										</TableCell>
										<TableCell className="px-3 py-2 tabular-nums">
											{r.ruleType === "fixed" && `${(Number(r.priceCents) / 100).toFixed(2)}`}
											{r.ruleType === "percentage" && `${r.discountPct}%`}
											{r.ruleType === "tiered" && "—"}
										</TableCell>
										<TableCell className="px-3 py-2 tabular-nums text-muted-foreground">
											{r.minQty || "—"}
										</TableCell>
										<TableCell className="px-3 py-2 text-right">
											<Button
												variant="ghost"
												size="sm"
												className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
												onClick={() => setRemoveTarget(r.id)}
											>
												<Trash2 className="h-3.5 w-3.5" />
											</Button>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				)}
			</div>

			{/* Add rule form */}
			{showAddRule && (
				<div className="rounded-lg border p-4">
					<h3 className="mb-3 text-sm font-medium">{t("pl_add_rule")}</h3>
					<div className="grid grid-cols-2 gap-3 md:grid-cols-3">
						<div className="space-y-1">
							<Label className="text-xs">{t("pl_rule_product")}</Label>
							<Input
								value={ruleForm.productId}
								onChange={(e) => setRuleForm({ ...ruleForm, productId: e.target.value })}
								placeholder="UUID"
							/>
						</div>
						<div className="space-y-1">
							<Label className="text-xs">{t("pl_rule_category")}</Label>
							<Input
								value={ruleForm.categoryId}
								onChange={(e) => setRuleForm({ ...ruleForm, categoryId: e.target.value })}
								placeholder="UUID"
							/>
						</div>
						<div className="space-y-1">
							<Label className="text-xs">{t("rule_type")}</Label>
							<Select
								value={ruleForm.ruleType}
								onValueChange={(v) => setRuleForm({ ...ruleForm, ruleType: v })}
							>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="fixed">{t("rule_fixed")}</SelectItem>
									<SelectItem value="percentage">{t("rule_percentage")}</SelectItem>
									<SelectItem value="tiered">{t("rule_tiered")}</SelectItem>
								</SelectContent>
							</Select>
						</div>
						{ruleForm.ruleType === "fixed" && (
							<div className="space-y-1">
								<Label className="text-xs">{t("pl_price_cents")}</Label>
								<Input
									type="number"
									value={ruleForm.priceCents}
									onChange={(e) => setRuleForm({ ...ruleForm, priceCents: Number(e.target.value) })}
								/>
							</div>
						)}
						{ruleForm.ruleType === "percentage" && (
							<div className="space-y-1">
								<Label className="text-xs">{t("pl_discount_pct")}</Label>
								<Input
									type="number"
									value={ruleForm.discountPct}
									onChange={(e) => setRuleForm({ ...ruleForm, discountPct: Number(e.target.value) })}
								/>
							</div>
						)}
						<div className="space-y-1">
							<Label className="text-xs">{t("min_qty")}</Label>
							<Input
								type="number"
								value={ruleForm.minQty}
								onChange={(e) => setRuleForm({ ...ruleForm, minQty: Number(e.target.value) })}
							/>
						</div>
					</div>
					<div className="mt-3 flex justify-end gap-2">
						<Button variant="outline" size="sm" onClick={() => setShowAddRule(false)}>
							{t("common:cancel")}
						</Button>
						<Button
							size="sm"
							onClick={() => addRuleMut.mutate()}
							disabled={addRuleMut.isPending}
						>
							{addRuleMut.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
							{t("common:add")}
						</Button>
					</div>
				</div>
			)}

			{/* Remove rule confirmation */}
			<AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("pl_remove_rule")}</AlertDialogTitle>
						<AlertDialogDescription>{t("pl_remove_rule_hint")}</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => removeTarget && removeRuleMut.mutate(removeTarget)}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							{t("common:delete")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
